import { skipToken } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Position } from '../../../shared/types/protocol.ts';
import { ProtocolDraft, ProtocolFinal } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';


export function useInitProtocol(
    protocol: ProtocolDraft | null,
    setProtocol: (protocol: ProtocolDraft) => void,
) {
    const searchParams = new URLSearchParams(window.location.search);
    const protocolId = searchParams.get('id');
    const baseProtocolId = searchParams.get('baseProtocolId');
    const isCopy = searchParams.get('isCopy') === 'true';
    const mode = searchParams.get('mode') as 'edit' | 'view' | 'review' ?? 'edit';


    const protocolFetch = trpc.protocols.getById.useQuery(
        (protocolId ?? baseProtocolId) ?? skipToken,
        { refetchOnWindowFocus: false },
    );

    const { data: baseProtocol } = trpc.protocols.getById.useQuery(
        (baseProtocolId ?? protocolFetch.data?.baseProtocol) ?? skipToken,
        { refetchOnWindowFocus: false },
    );

    useEffect(() => {
        if (protocol) return;
        if (protocolId == null && baseProtocolId == null) {
            setProtocol(newDraft());
        } else if (protocolId && protocolFetch.data) {
            setProtocol(protocolFetch.data);
        } else if (baseProtocolId && protocolFetch.data) {
            setProtocol(draftFromBase(protocolFetch.data as ProtocolFinal, isCopy));
        }
    }, [protocol, protocolId, baseProtocolId, protocolFetch.data, setProtocol, isCopy]);

    useEffect(() => {
        if (protocol?._id && !protocolId) {
            // Update the URL when the protocol ID becomes available (will make refresh work).
            window.history.replaceState(null, '', `/protocol-view?id=${protocol._id}&mode=edit`);
        }
    }, [protocol?._id, protocolId])

    const newDraft = (): ProtocolDraft => {
        const generalInspection: Position = {
            id: 1,
            type: 'generalInspection',
            description: 'General visual inspection',
            page: 1,
            markers: [],
            positionNumber: '1',
        }

        return {
            status: 'draft',
            partName: '',
            partRevision: '',
            partDisplayName: '',
            programs: [],
            positions: [generalInspection],
        }
    }

    const draftFromBase = (protocol: ProtocolFinal, isCopy: boolean): ProtocolDraft => {
        const newDraft = {
            ...protocol,
            _id: undefined,
            baseProtocol: protocol._id,
            isCopy,
        };
        newDraft.status = 'draft';
        delete newDraft.createTime;
        delete newDraft.version;
        delete newDraft.analysisVersion;
        delete newDraft.isDisabled;
        delete newDraft.reviews;
        delete newDraft.requiresPlanReview;
        delete newDraft.user;
        delete newDraft.changes;
        return newDraft;
    }


    return {
        isProtocolLoading: !protocol && protocolFetch.isLoading,
        protocolError: !protocol && protocolFetch.error,
        mode,
        isViewMode: mode === 'view',
        initialProtocolId: protocolId ?? baseProtocolId,
        baseProtocol: baseProtocol ?? null,
    }
}
